import { useState, type ReactNode } from 'react'
import type { DocControlEntry } from '../lib/api.ts'

/** The revision a document currently sits at: the last row Astute recorded. */
export function currentRevision(docControl: DocControlEntry[]): DocControlEntry | null {
  return docControl.length > 0 ? docControl[docControl.length - 1] : null
}

function Fact({ label, value }: { label: string; value: string }) {
  if (!value) return null
  return (
    <span className="docctl__fact">
      <b>{label}</b>
      <span>{value}</span>
    </span>
  )
}

/**
 * The masthead of a rendered document: what kind of thing it is, what it is
 * called, and how current it is.
 *
 * The document-control line answers the question an assessor actually asks —
 * which version is this, when was it last reviewed, when is it next due, who
 * owns it — from Astute's own revision table rather than anything we compute.
 * The full history sits behind a disclosure because "what changed and when" is
 * the follow-up question, not the first one.
 */
export function DocHeader({
  title, badges, docControl = [], actions,
}: {
  title: string
  badges?: string[]
  docControl?: DocControlEntry[]
  actions?: ReactNode
}) {
  const [showHistory, setShowHistory] = useState(false)
  const current = currentRevision(docControl)
  // Every revision is a row worth reading; the current one is already summarised
  // in the line above, so the table only earns its place once there is a past.
  const hasHistory = docControl.length > 1

  return (
    <header className="doc-head">
      {badges && badges.length > 0 && (
        <div className="doc-head__badges">
          {badges.map((b, i) => (
            <span key={b} className={i === 0 ? 'badge' : 'badge badge--quiet'}>{b}</span>
          ))}
        </div>
      )}

      <h1 className="doc-head__title">{title}</h1>

      {current && (
        <div className="docctl">
          <Fact label="Version" value={current.version} />
          <Fact label="Reviewed" value={current.dateReviewed} />
          <Fact label="Next review" value={current.nextReviewDate} />
          <Fact label="Owner" value={current.roleResponsible} />
          {hasHistory && (
            <button
              type="button"
              className="docctl__more"
              aria-expanded={showHistory}
              onClick={() => setShowHistory((open) => !open)}
            >
              {showHistory ? 'Hide history' : `Revision history (${docControl.length})`}
            </button>
          )}
        </div>
      )}

      {showHistory && hasHistory && (
        <div className="history">
          <div className="history__row history__row--head">
            <span>Version</span>
            <span>Reviewed</span>
            <span>What changed</span>
          </div>
          {/* Newest first: the most recent change is the one being asked about. */}
          {[...docControl].reverse().map((r) => (
            <div className="history__row" key={r.version}>
              <b>{r.version}</b>
              <i>{r.dateReviewed}</i>
              <span>{r.modifications}</span>
            </div>
          ))}
        </div>
      )}

      {actions && <div className="tool__actions doc-head__actions">{actions}</div>}
    </header>
  )
}
